import plans from "@storage/app/plans.json";
import http from "@/utils/service/http";
import router from "@/router/index";
import { isPlainObject } from "lodash";
import {
    planByName,
    planFeaturesCount,
    planHasFeatures,
    plansByLocale,
} from "@/utils/service/billingPlans";
import { setAmplitudeUser } from "@/utils/amplitude";

export default {
    state: {
        email: null,
        emailVerified: null,
        id: null,
        uuid: null,
        card_brand: null,
        card_last_four: null,
        subscribed_to_newsletter: false,
        avatar: null,
        subscriptions: [],
        nonActiveSubscriptions: [],
        days: 0,
        documents: 0,
        projects: 0,
        contracts: 0,
        tosagree: true,
        locale: "en",
        country: "US",
        business: true,
    },
    getters: {
        plansToUpgrade: (state, getters) => (params = "en") => {
            let planLocale = "en";
            let features = [];

            ({ planLocale, features } = transformParams(params));

            let plansForLocale = plansByLocale(planLocale);

            if (Array.isArray(features) && features.length > 0) {
                plansForLocale = plansForLocale.filter((plan) =>
                    planHasFeatures(plan, features)
                );
            }

            const currentPlan = getters.getCurrentSubscriptionPlan(
                null,
                planLocale
            );

            const currentPlanFeaturesCount = planFeaturesCount(currentPlan);

            return plansForLocale.filter((plan) =>
                plan.slug === currentPlan?.slug
                    ? false
                    : planFeaturesCount(plan) > currentPlanFeaturesCount
            );
        },
        planToUpgrade: (state, getters) => (params = "en") => {
            return getters.plansToUpgrade(params).shift(); // getters.plansToUpgrade(params).pop();
        },
        planHasFeatures: (state, getters) => (params = "en") => {
            let planLocale = "en";
            let features = [];

            ({ planLocale, features } = transformParams(params));

            const currentPlan = getters.getCurrentSubscriptionPlan();

            return checkIfPlanHasFeatures(currentPlan, features);
        },
        getCurrentSubscription: (state) => (locale = null) => {
            if (!locale) locale = router?.currentRoute?.params?.locale || "en";

            const subscriptions = sortSubscriptions(
                state.subscriptions,
                locale
            );

            const subscription = [...subscriptions].pop();

            if (
                locale &&
                subscription?.plans
                    ?.map(({ locale }) => locale)
                    .includes(locale)
            )
                return subscription;

            const plans = plansByLocale(locale);

            const defaultPlan = findFreePlan(plans);

            if (!defaultPlan) return null;

            return {
                billing_period: "monthly",
                created_at: "null",
                updated_at: "null",
                ends_at: null,
                id: null,
                items: [],
                locale: locale,
                name: defaultPlan?.name,
                quantity: 1,
                slug: defaultPlan?.slug,
                stripe_id: null,
                stripe_price:
                    defaultPlan?.prices[0]?.stripe_price_id ||
                    defaultPlan?.prices[0]?.paypal_price_id,
                stripe_status: "active",
                trial_ends_at: null,
            };
        },
        getCurrentSubscriptionName(state, getters) {
            const locale = router?.currentRoute?.params?.locale || "en";

            return getters.getCurrentSubscriptionPlan(null, locale)?.name;
        },
        getCurrentSubscriptionPlan: (state, getters) => (
            planSlug = null,
            planLocale = null
        ) => {
            if (!planSlug) {
                const planLocale =
                    planLocale || router?.currentRoute?.params?.locale || "en";
                const currentSubscription = getters.getCurrentSubscription(
                    planLocale
                );
                const plansForLocale = plansByLocale(planLocale);

                let plan = Object.values(plansForLocale).find(
                    ({ name }) => name === currentSubscription?.name
                );

                if (plan) return plan;

                const currentPlansSlugs =
                    currentSubscription?.plans
                        ?.filter(({ locale }) => locale === planLocale)
                        ?.map(({ slug }) => slug) || [];

                const currentPlans = plansForLocale.filter(({ slug }) =>
                    currentPlansSlugs.includes(slug)
                );

                return currentPlans.pop();
            }

            return plans.find(({ slug }) => slug === planSlug);
        },
        getCurrentSubscriptionPricing(state, getters) {
            const locale = router?.currentRoute?.params?.locale || "en";
            const currentSubscription = getters.getCurrentSubscription(locale);
            const plan = getters.getCurrentSubscriptionPlan(null, locale);

            const subscriptionBillingPeriodForPlan =
                currentSubscription.plans
                    ?.filter(({ locale }) => locale === locale)
                    .pop()?.billing_period || "monthly";

            return (
                plan.prices.find(
                    ({ billing_period }) =>
                        billing_period === subscriptionBillingPeriodForPlan
                ) || null
            );
        },
        showLanguageSelect(state) {
            return state.country === "LT" || state.locale === "lt";
        },
    },
    mutations: {
        SET_CLIENT_DATA(state, data = {}) {
            if (data.email) state.email = data.email;
            if (data.id) state.id = data.id;
            if (data.uuid) state.uuid = data.uuid;
            if (data.subscriptions) state.subscriptions = data.subscriptions;
            state.emailVerified = data.email_verified;
            state.nonActiveSubscriptions = data.nonActiveSubscriptions;
            state.subscribed_to_newsletter = data.subscribed_to_newsletter;
            state.card_brand = data.pm_type;
            state.card_last_four = data.pm_last_four;
            state.avatar = data.avatar;
            state.days = data.days;
            state.documents = data.documents;
            state.projects = data.projects;
            state.contracts = data.contracts;
            state.tosagree = data.tosagree;
            state.chat_hash = data.chat_hash;
            state.locale = data.locale;
            state.country = data.country;

            state.business = data.business;
        },
        SET_SUBSCRIPTIONS(state, subscriptions) {
            state.subscriptions = subscriptions;
        },
        UPDATE_SUBSCRIBED_TO_NEWSLETTER(state, data = false) {
            state.subscribed_to_newsletter = data;
        },
        UPDATE_SUBSCRIPTION(state, newSubscription) {
            state.subscriptions = state.subscriptions.filter(
                ({ stripe_id }) => stripe_id !== newSubscription.stripe_id
            );

            // Object.entries(newSubscription).forEach(([key, value]) => {
            //     subscription[key] = value;
            // })

            state.subscriptions.push(newSubscription);
        },
    },
    actions: {
        async clientDataIndex(context) {
            if (!context.rootState.auth.token) return;

            const data = await http.get("/api/v1/me");

            context.commit("SET_CLIENT_DATA", data);

            setChatUser(data);
            setAmplitudeUser(data);

            return data;
        },
        async clientSubscriptions(context) {
            if (!context.rootState.auth.token) return;

            const subscriptions = await http.get(
                "/api/v1/payments/subscriptions"
            );

            context.commit("SET_SUBSCRIPTIONS", subscriptions);

            return subscriptions;
        },
    },
};

const sortSubscriptions = (subscriptions, locale = "en") => {
    return [...subscriptions]
        .sort((a, b) => {
            return a.id === b.id ? 0 : a.id > b.id ? 1 : -1;
        })
        .sort((a, b) => {
            // const aLocales = a.locale.split(",");
            const aLocales = a?.plans?.map(({ locale }) => locale) || [];
            const bLocales = b?.plans?.map(({ locale }) => locale) || [];
            // const bLocales = b.locale.split(",");

            if (aLocales.map((l) => l.trim()).includes(locale)) return 1;
            if (bLocales.map((l) => l.trim()).includes(locale)) return -1;

            return 0;
        });
};

const transformParams = (params) => {
    let planLocale = "en";
    let locale = "en";
    let features = [];

    if (isPlainObject(params)) {
        ({ locale, features } = params);
        planLocale = locale || planLocale;
    } else {
        planLocale = params;
    }

    return {
        features,
        planLocale,
    };
};

const checkIfPlanHasFeatures = (plan, features) => {
    return features.reduce((current, requiredFeature) => {
        return (
            current &&
            !!plan?.features.find(({ slug }) => slug === requiredFeature)
        );
    }, true);
};

const setChatUser = (data) => {
    const setChat = setInterval(() => {
        try {
            if (typeof Tawk_API !== "undefined") {
                Tawk_API.setAttributes({
                    email: data.email,
                    hash: data.chat_hash,
                });

                clearInterval(setChat);
            }
        } catch {}
    }, 2000);
};

const findFreePlan = (plans) => {
    return plans.find((plan) => plan.prices.find(({ price }) => price === 0));
};
